/**
 * Documents to show after a draft's document list comes back.
 * A stale response (the resident already opened a different pet) must be ignored.
 * An empty list is a real result: this pet has no uploads yet.
 */
export function restoredDraftDocuments<T>(
  fetched: T[] | null | undefined,
  fetchMatchesCurrentDraft: boolean,
): T[] | null {
  if (!fetchMatchesCurrentDraft) return null;
  return fetched ?? [];
}

export type DraftDocumentLike = {
  id?: string;
  docType?: string;
  uploadStatus?: string;
};

/**
 * Combine the server snapshot with files the resident added while that request
 * was in flight. One row per document type.
 *
 * A just-finished upload is kept even though the upload view marks it
 * `restoredFromServer` — that flag is not "this row came from the GET".
 * A failed attempt does not stay beside a file the server already has, or the
 * visible row looks uploaded while submit still sees the hidden failure.
 */
export function mergeRestoredDraftDocuments<T extends DraftDocumentLike>(
  restored: T[],
  current: T[] | undefined,
): T[] {
  const local = (current || []).filter(
    (doc) => doc && !String(doc.id || '').startsWith('restored_'),
  );
  const localByType = new Map<string, T>();
  const localUntyped: T[] = [];
  for (const doc of local) {
    if (!doc.docType) {
      localUntyped.push(doc);
      continue;
    }
    localByType.set(doc.docType, doc);
  }

  const restoredTypes = new Set(
    restored.map((doc) => doc.docType).filter((type): type is string => Boolean(type)),
  );
  const keptRestored = restored.filter((doc) => {
    if (!doc.docType) return true;
    const localDoc = localByType.get(doc.docType);
    if (!localDoc) return true;
    return localDoc.uploadStatus === 'failed';
  });
  const keptLocal = [...localByType.values()].filter((doc) => {
    if (doc.uploadStatus !== 'failed') return true;
    return !doc.docType || !restoredTypes.has(doc.docType);
  });
  return [...keptRestored, ...keptLocal, ...localUntyped];
}
